import {
  getCaptureTypesForProduct,
  hasAddedTwinOrDataSim,
  isMobileProduct,
  isVoiceProduct,
} from '@app/sales/capture-type-utils';
import { MobilePaymentType } from '@app/sales/enums/mobile-payment-type';
import { MobilePhoneProduct } from '@app/sales/models/mobilephone-product';
import { CaptureType } from '@app/sales/models/order-capture.model';
import { Product } from '@app/shared/models/product.model';
import { VoiceProduct } from '@app/shared/models/voice-product.model';
import { SubscriptionHelperService } from './services/subscription-helper.service';

const requiresVerification = new Set<CaptureType>([
  CaptureType.CreateSubscriptionWithPorting,
  CaptureType.CreateSubscriptionWithCustomerSelectedANumber,
  CaptureType.ChangeSimCard,
  CaptureType.CreateCellPhoneBundleWithCustomerSelectedANumber,
  CaptureType.CreateCellPhoneBundleWithPorting,
  CaptureType.CreateCellPhoneBundleWithExistingSubscription,
  CaptureType.CreateAccessoryBundleWithCustomerSelectedANumber,
  CaptureType.CreateAccessoryBundleWithPorting,
  CaptureType.CreateAccessoryBundleWithExistingSubscription,
]);

const maybeRequiresVerification = new Set<CaptureType>([
  CaptureType.ChangeSubscriptionOnExistingCustomer,
  CaptureType.ChangeSubscriptionOnExistingCustomerWithSubscriber,
  CaptureType.UpdateServicesOnExistingSubscription,
]);

export const orderRequiresBankIdVerification = (products: Product[], customerHasSecretNumber: boolean): boolean => {
  const captureTypes = getCaptureTypes(products);
  const captureTypeRequiresVerification = captureTypes.some(type => requiresVerification.has(type));
  if (captureTypeRequiresVerification) {
    return true;
  }

  if (customerHasSecretNumber) {
    return true;
  }

  if (orderContainsMobilBytte(products)) {
    return true;
  }

  if (orderContainsChangeSubWithAnnualBinding(products, captureTypes)) {
    return true;
  }

  const requiresVerificationBasedOnServices =
    captureTypes.some(type => maybeRequiresVerification.has(type)) &&
    products.some(
      p =>
        isVoiceProduct(p) &&
        (hasAddedTwinOrDataSim(p) ||
          hasRemovedDataOrTwinSim(p as VoiceProduct) ||
          productHasChangesInBarringServices(p as VoiceProduct))
    );
  if (requiresVerificationBasedOnServices) {
    return true;
  }

  return false;
};

const getCaptureTypes = (products: Product[]): CaptureType[] => {
  const voiceProducts = products.filter(isVoiceProduct) as VoiceProduct[];
  const captureTypes = products
    .map(product => getCaptureTypesForProduct(product, voiceProducts))
    .reduce((all, types) => all.concat(types), []);
  return captureTypes.filter(type => type !== CaptureType.NotImplemented);
};

const orderContainsMobilBytte = (products: Product[]): boolean => {
  const mobilBytteProducts = products
    .filter(isMobileProduct)
    .map(p => p as MobilePhoneProduct)
    .filter(p => p.paymentType === MobilePaymentType.EquipmentUpgrade);

  return mobilBytteProducts.length > 0;
};

const orderContainsChangeSubWithAnnualBinding = (products: Product[], captureTypes: CaptureType[]): boolean => {
  const changeSubWithArsavtale = products
    .filter(
      (p, i) =>
        [
          CaptureType.ChangeSubscriptionOnExistingCustomer,
          CaptureType.ChangeSubscriptionOnExistingCustomerWithSubscriber,
        ].includes(captureTypes[i]) && isVoiceProduct(p)
    )
    .map(p => p as VoiceProduct)
    .some(p => hasAnnualBinding(p));

  return changeSubWithArsavtale;
};

export const hasAnnualBinding = (product: VoiceProduct): boolean => {
  const { offering } = product;
  if (!offering) return false;
  return offering.commitmentPeriod >= 12 || offering.variant === 'SimBindingAgreement';
};

const hasRemovedDataOrTwinSim = (product: VoiceProduct): boolean => {
  const removedServices = VoiceProduct.removedServices(product);
  return removedServices.some(service => service.isDataSimCard || service.isTwinSimCard);
};

export const productHasChangesInBarringServices = (product: VoiceProduct): boolean => {
  return SubscriptionHelperService.getChangedServicesOnExistingVoiceProduct(product).some(
    service => service.isBarringService
  );
};
